
import React, { useState, useEffect } from 'react';
import CustomerPortal from './components/CustomerPortal';
import BusinessPortal from './components/BusinessPortal';
import OwnerPortal from './components/OwnerPortal';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import LandingPage from './components/LandingPage';
import CreateEventWizard from './components/CreateEventWizard';
import BillingModal from './components/BillingModal';
import { MOCK_INDIVIDUALS, MOCK_BUSINESSES, COUNTRIES_CURRENCIES, BASE_PRICING } from './constants';
import { Individual, Business, UserProfile, VendorInquiry, ActivityLog, Transaction, Suggestion } from './types';
import { LanguageProvider } from './contexts/LanguageContext';
import { supabase } from './lib/supabase';

const AppContent: React.FC = () => {
  // Auth State
  const [showLanding, setShowLanding] = useState(true);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [authView, setAuthView] = useState<'login' | 'signup'>('login');
  const [isLoading, setIsLoading] = useState(true);

  // App State
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [myBusinessProfile, setMyBusinessProfile] = useState<Business | null>(null);
  const [vendorInquiries, setVendorInquiries] = useState<VendorInquiry[]>([]);
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  
  // Dynamic Pricing State
  const [appPricing, setAppPricing] = useState({
    individual: BASE_PRICING.INDIVIDUAL,
    business: BASE_PRICING.BUSINESS
  });

  // Dynamic Services managed by Admin
  const [globalServices, setGlobalServices] = useState<string[]>([
    "Hall / Venue Services",
    "Catering Services",
    "Photography / Videography",
    "Transportation Services",
    "Decoration Services"
  ]);
  
  // Portal State
  const [portalActiveTab, setPortalActiveTab] = useState<'overview' | 'vendors' | 'gallery' | 'events'>('overview');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(30);

  // Helper for better error stringification
  const getErrorMessage = (error: any) => {
    if (!error) return "Unknown error";
    return error.message || (typeof error === 'object' ? JSON.stringify(error, null, 2) : String(error));
  };

  // Consistent logic for filtering events by current user
  const getUserEvents = (allEvents: Individual[], email: string) => {
    if (!email || !allEvents) return [];
    const cleanEmail = email.toLowerCase().trim();
    return allEvents.filter(i => i.email && i.email.toLowerCase().trim() === cleanEmail);
  };

  // 1. Initialize Auth Session
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const cleanEmail = session.user.email?.toLowerCase().trim() || '';
          const { data: profile, error } = await supabase.from('profiles').select('*').eq('email', cleanEmail).single();
          if (!error && profile) {
            setCurrentUser(profile);
          } else {
            const meta = session.user.user_metadata as UserProfile;
            setCurrentUser({
              ...meta,
              email: cleanEmail,
              role: meta.role || 'individual',
              isPaid: meta.isPaid || false
            });
          }
          setShowLanding(false);
        }
      } catch (err) {
        console.error("Auth init error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const cleanEmail = session.user.email?.toLowerCase().trim() || '';
        const { data: profile } = await supabase.from('profiles').select('*').eq('email', cleanEmail).single();
        if (profile) {
          setCurrentUser(profile);
        }
      } else {
        setCurrentUser(null);
      }
    });

    return () => authListener.subscription.unsubscribe();
  }, []);

  // 2. Fetch Data from Supabase
  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      const { data: bizData } = await supabase.from('businesses').select('*');
      if (bizData) setBusinesses(bizData);

      const { data: indData, error: indError } = await supabase.from('individuals').select('*');
      if (indError) console.error("Error fetching events:", getErrorMessage(indError));
      if (indData) setIndividuals(indData);

      const { data: sugData } = await supabase.from('suggestions').select('*').order('timestamp', { ascending: false });
      if (sugData) setSuggestions(sugData);

      const { data: inqData } = await supabase.from('vendor_inquiries').select('*');
      if (inqData) setVendorInquiries(inqData);

      const { data: actData } = await supabase.from('activity_logs').select('*').order('timestamp', { ascending: false });
      if (actData) setActivities(actData);

      const { data: txData } = await supabase.from('transactions').select('*').order('timestamp', { ascending: false });
      if (txData) setTransactions(txData);
    };

    fetchData();
  }, [currentUser]);

  // Sync state to specific business profile
  useEffect(() => {
    if (currentUser?.role === 'business') {
      const userEmail = currentUser.email.toLowerCase().trim();
      const profile = (businesses || []).find(b => b.email && b.email.toLowerCase().trim() === userEmail);
      if (profile) {
        setMyBusinessProfile(profile);
      } else if (!isLoading) {
        const newProfile: Business = {
          id: crypto.randomUUID(),
          name: currentUser.name,
          category: currentUser.businessCategory || 'Venue / Hall Services',
          contactName: currentUser.name,
          email: userEmail,
          phone: '',
          rating: 0,
          city: currentUser.country,
          country: currentUser.country,
          description: '',
          services: [],
          images: []
        };
        handleUpdateBusinessProfile(newProfile);
      }

      if (!currentUser.isPaid && currentUser.createdAt) {
        const created = new Date(currentUser.createdAt);
        const now = new Date();
        const diffTime = now.getTime() - created.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const remaining = 30 - diffDays;
        setTrialDaysRemaining(remaining);
        if (remaining <= 0) setIsBillingModalOpen(true);
      }
    }
  }, [currentUser, businesses, isLoading]);

  const logActivity = async (
    actionType: ActivityLog['actionType'],
    description: string,
    overrideUser?: UserProfile
  ) => {
    const user = overrideUser || currentUser;
    if (!user) return;

    const newActivity: ActivityLog = {
      id: crypto.randomUUID(),
      userId: user.email.toLowerCase().trim(),
      userName: user.name,
      userRole: user.role,
      actionType,
      description,
      timestamp: new Date().toISOString(),
    };

    const { error } = await supabase.from('activity_logs').insert([newActivity]);
    if (!error) {
      setActivities(prev => [newActivity, ...prev]);
    }
  };

  const handleLogin = async (role: 'business' | 'individual', identifier: string) => {
    const cleanEmail = identifier.toLowerCase().trim();
    const { data: profile } = await supabase.from('profiles').select('*').eq('email', cleanEmail).single();
    
    if (!profile && identifier !== 'admin@eventmanager.com') {
      alert("Account not found. Please sign up first.");
      return;
    }

    if (identifier === 'admin@eventmanager.com') {
        const admin: UserProfile = { name: 'Super Admin', email: identifier, role: 'owner', country: 'Global', currency: 'USD', createdAt: new Date().toISOString(), isPaid: true };
        setCurrentUser(admin);
        logActivity('login', 'Owner Access Granted', admin);
    } else {
        setCurrentUser(profile);
        logActivity('login', 'Logged in successfully', profile);
    }
    setShowLanding(false);
  };

  const handleSignup = async (role: 'business' | 'individual', name: string, identifier: string, country: string, businessType?: string) => {
    const cleanEmail = identifier.toLowerCase().trim();
    const { data: existingProfile } = await supabase.from('profiles').select('email').eq('email', cleanEmail).single();
    if (existingProfile) {
        alert("This email is already registered. Please sign in instead.");
        setAuthView('login');
        return;
    }

    const selectedCurrency = COUNTRIES_CURRENCIES.find(c => c.country === country)?.currency || 'USD';
    const profile: UserProfile = { 
      name, email: cleanEmail, role, country, currency: selectedCurrency, 
      businessCategory: businessType, createdAt: new Date().toISOString(), isPaid: false 
    };
    
    const { error } = await supabase.from('profiles').insert([profile]);
    if (error) {
        alert("Signup Error: " + getErrorMessage(error));
        return;
    }

    setCurrentUser(profile);
    logActivity('login', `Created account via signup`, profile);
    setShowLanding(false);
  };

  const handleLogout = async () => {
    setIsLoading(true);
    await supabase.auth.signOut();
    setCurrentUser(null);
    setShowLanding(true);
    setPortalActiveTab('overview');
    setIsLoading(false);
  };

  const handleUpdateBusinessProfile = async (updated: Business) => {
    const cleanEmail = updated.email?.toLowerCase().trim();
    const profileToSave = { ...updated, email: cleanEmail };
    const { error } = await supabase.from('businesses').upsert([profileToSave]);
    if (error) {
      alert("Business Update Error: " + getErrorMessage(error));
      return;
    }
    setBusinesses(prev => prev.map(b => b.id === updated.id ? profileToSave : b));
    setMyBusinessProfile(profileToSave);
    logActivity('update', `Updated business profile: ${updated.name}`);
  };

  const handleSaveNewEvent = async (individual: Individual) => {
    if (!currentUser) return;
    const userEmail = currentUser.email.toLowerCase().trim();
    
    // RE-VERIFY LIMIT: Double-check database count to prevent bypass
    const { count, error: countError } = await supabase
      .from('individuals')
      .select('*', { count: 'exact', head: true })
      .eq('email', userEmail);
    
    if (countError) {
      alert("Count Verification Error: " + countError.message);
      return;
    }

    const currentCount = count || 0;
    
    if (currentCount >= 1 && !currentUser.isPaid) {
        alert(`You have already used your 1 free event. Please upgrade to Pro (${appPricing.individual} USD) to orchestrate more events.`);
        setIsWizardOpen(false);
        setIsBillingModalOpen(true);
        return;
    }

    const eventToSave = {
      ...individual,
      email: userEmail,
      dueDate: individual.dueDate === '' ? null : individual.dueDate,
      weddingDate: individual.weddingDate === '' ? null : individual.weddingDate
    };

    const { data, error } = await supabase.from('individuals').insert([eventToSave]).select();
    
    if (error) {
      alert("Database Save Error: " + getErrorMessage(error));
      return;
    }

    if (data && data.length > 0) {
      setIndividuals(prev => [data[0], ...prev]);
      logActivity('create', `Created new event: ${data[0].eventName || data[0].eventType}`);
      setIsWizardOpen(false);
    }
  };

  const handleSaveIndividual = async (individual: Individual) => {
    const cleanEmail = individual.email?.toLowerCase().trim();
    const eventToSave = {
      ...individual,
      email: cleanEmail,
      dueDate: individual.dueDate === '' ? null : individual.dueDate,
      weddingDate: individual.weddingDate === '' ? null : individual.weddingDate
    };

    const { data, error } = await supabase.from('individuals').upsert([eventToSave]).select();
    if (error) {
      alert("Database Update Error: " + getErrorMessage(error));
      return;
    }

    if (data && data.length > 0) {
      setIndividuals(prev => {
        const exists = prev.find(c => c.id === data[0].id);
        if (exists) return prev.map(c => c.id === data[0].id ? data[0] : c);
        return [data[0], ...prev];
      });
      logActivity('update', `Updated event details: ${data[0].eventName || data[0].eventType}`);
    }
  };

  const handleDeleteIndividual = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      const { error } = await supabase.from('individuals').delete().eq('id', id);
      if (error) {
        alert("Delete Error: " + getErrorMessage(error));
        return;
      }
      setIndividuals(prev => prev.filter(c => c.id !== id));
    }
  };
  
  const handleDeleteBusiness = async (id: string) => {
    if (window.confirm("Are you sure you want to permanently delete this business account?")) {
      const { error } = await supabase.from('businesses').delete().eq('id', id);
      if (error) {
        alert("Delete Error: " + getErrorMessage(error));
        return;
      }
      setBusinesses(prev => prev.filter(b => b.id !== id));
    }
  };

  const handleSendVendorInquiry = async (inquiry: VendorInquiry) => {
    const { error } = await supabase.from('vendor_inquiries').insert([inquiry]);
    if (error) {
      alert("Inquiry Error: " + getErrorMessage(error));
      return;
    }
    setVendorInquiries(prev => [...prev, inquiry]);
    logActivity('inquiry', `Sent inquiry to ${inquiry.businessName}`);
  };

  const handlePaymentSuccess = async (amount: number, currency: string) => {
    if (currentUser) {
      const userEmail = currentUser.email.toLowerCase().trim();
      
      const { error } = await supabase.from('profiles').update({ isPaid: true }).eq('email', userEmail);
      if (error) {
        console.error("Profile Update Error:", error.message);
      }

      setCurrentUser(prev => prev ? ({ ...prev, isPaid: true }) : null);
      
      const newTransaction: Transaction = {
        id: crypto.randomUUID(), 
        userId: userEmail, 
        userName: currentUser.name,
        userRole: currentUser.role as 'business' | 'individual', 
        amount, 
        currency,
        timestamp: new Date().toISOString(), 
        description: currentUser.role === 'business' ? 'Monthly Subscription' : 'Pro Event Upgrade'
      };
      
      const { error: txError } = await supabase.from('transactions').insert([newTransaction]);
      if (!txError) {
        setTransactions(prev => [newTransaction, ...prev]);
      }
      
      setIsBillingModalOpen(false);
      logActivity('payment', `Account upgraded to Pro status. Transaction of ${amount} ${currency} authorized.`);
    }
  };

  const handleSendSuggestion = async (content: string) => {
    if (!currentUser) return;
    const userEmail = currentUser.email.toLowerCase().trim();
    const newSuggestion: Suggestion = {
      id: crypto.randomUUID(), 
      userId: userEmail, 
      userName: currentUser.name,
      userRole: currentUser.role as 'business' | 'individual', 
      content,
      timestamp: new Date().toISOString(), 
      status: 'new'
    };
    const { error } = await supabase.from('suggestions').insert([newSuggestion]);
    if (!error) {
      setSuggestions(prev => [newSuggestion, ...prev]);
    }
  };

  const handleUpdateSuggestionStatus = async (id: string, status: 'new' | 'reviewed') => {
    const { error } = await supabase.from('suggestions').update({ status }).eq('id', id);
    if (!error) {
      setSuggestions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    }
  };

  const handleAddGlobalService = (name: string) => {
    if (!globalServices.includes(name)) {
      setGlobalServices(prev => [...prev, name]);
    }
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-wedding-50">
    <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-wedding-600"></div>
        <p className="text-wedding-800 font-serif font-bold text-lg animate-pulse">Initializing Portal...</p>
    </div>
  </div>;

  const totalUserCount = (individuals?.length || 0) + (businesses?.length || 0);
  const userEmail = currentUser?.email?.toLowerCase().trim() || "";

  return (
    <LanguageProvider>
      {showLanding ? (
        <LandingPage onGetStarted={() => setShowLanding(false)} extraServices={globalServices} pricing={appPricing} />
      ) : !currentUser ? (
        authView === 'login' 
          ? <LoginPage 
              onLogin={handleLogin} 
              onNavigateToSignup={() => setAuthView('signup')} 
              onBackToHome={() => setShowLanding(true)}
            />
          : <SignupPage 
              onSignup={handleSignup} 
              onNavigateToLogin={() => setAuthView('login')} 
              onBackToHome={() => setShowLanding(true)}
            />
      ) : currentUser.role === 'owner' ? (
        <OwnerPortal
          onLogout={handleLogout} userName={currentUser.name} activities={activities}
          transactions={transactions} suggestions={suggestions} onUpdateSuggestionStatus={handleUpdateSuggestionStatus}
          usersCount={totalUserCount}
          individuals={individuals} businesses={businesses} onDeleteIndividual={handleDeleteIndividual} onDeleteBusiness={handleDeleteBusiness}
          globalServices={globalServices} onAddGlobalService={handleAddGlobalService}
          pricing={appPricing} onUpdatePricing={setAppPricing}
        />
      ) : currentUser.role === 'individual' ? (
        <>
          <CustomerPortal 
            onLogout={handleLogout} userName={currentUser.name} onAddEvent={async () => {
                const myEventsCount = getUserEvents(individuals, userEmail).length;
                // STRICT CHECK: 1 free, more requires Pro
                if (myEventsCount >= 1 && !currentUser.isPaid) {
                    setIsBillingModalOpen(true);
                } else {
                    setIsWizardOpen(true);
                }
            }} 
            events={getUserEvents(individuals, userEmail)}
            onUpdateEvent={handleSaveIndividual} onDeleteEvent={handleDeleteIndividual}
            activeTab={portalActiveTab} setActiveTab={setPortalActiveTab}
            vendorInquiries={vendorInquiries} onSendVendorInquiry={handleSendVendorInquiry}
            activities={activities.filter(a => a.userId && a.userId.toLowerCase().trim() === userEmail)}
            onSendSuggestion={handleSendSuggestion}
          />
          <CreateEventWizard isOpen={isWizardOpen} onSave={handleSaveNewEvent} onCancel={() => setIsWizardOpen(false)} initialEmail={userEmail} existingEvents={individuals} />
          <BillingModal isOpen={isBillingModalOpen} role="individual" onClose={() => setIsBillingModalOpen(false)} onSuccess={handlePaymentSuccess} initialCountry={currentUser.country} pricing={appPricing} />
        </>
      ) : (
        <>
          {myBusinessProfile ? (
            <BusinessPortal 
              onLogout={handleLogout} userName={currentUser.name} individuals={individuals} businesses={businesses}
              onAddIndividual={() => setIsWizardOpen(true)} onEditIndividual={() => {}} onDeleteIndividual={handleDeleteIndividual}
              onSaveIndividual={handleSaveIndividual} onAddBusiness={() => {}} isWizardOpen={isWizardOpen} setIsWizardOpen={setIsWizardOpen}
              onSaveNewEvent={handleSaveNewEvent} trialDaysRemaining={trialDaysRemaining} isPaid={currentUser.isPaid}
              activities={activities.filter(a => a.userId && a.userId.toLowerCase().trim() === userEmail)}
              userCountry={currentUser.country} userCategory={currentUser.businessCategory} myBusinessProfile={myBusinessProfile}
              onUpdateBusinessProfile={handleUpdateBusinessProfile} onSendSuggestion={handleSendSuggestion}
            />
          ) : (
             <div className="min-h-screen flex items-center justify-center bg-wedding-50">
               <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-wedding-600"></div>
                  <p className="text-wedding-800 text-sm font-bold uppercase tracking-widest animate-pulse">Syncing Business Node...</p>
               </div>
             </div>
          )}
          <BillingModal isOpen={isBillingModalOpen} role="business" onClose={() => setIsBillingModalOpen(false)} onSuccess={handlePaymentSuccess} initialCountry={currentUser.country} pricing={appPricing} />
        </>
      )}
    </LanguageProvider>
  );
};

const App: React.FC = () => <AppContent />;
export default App;
